import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Star, ArrowRight, Ruler, ShieldCheck, X, ZoomIn, FolderPlus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

function SuggestedProductsSection({ codes, currentProductCode }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/suggested-products", { params: { codes: codes.join(",") } })
      .then(r => { if (alive) setItems((r.data || []).filter(x => (x.our_code || "").toUpperCase() !== (currentProductCode || "").toUpperCase())); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [codes, currentProductCode]);

  if (!loading && items.length === 0) return null;

  return (
    <section className="mt-20 border-t border-[#1A1A1A]/10 pt-16" data-testid="pairs-well-with">
      <p className="label-eyebrow text-[#C9A968]">Curated Companions</p>
      <h2 className="font-editorial text-4xl tracking-editorial mt-2 mb-10">Pairs Well With</h2>
      {loading ? (
        <p className="text-[#8A8A8A] text-sm">Loading suggestions…</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
          {items.map(sp => (
            <Link
              key={sp.id}
              to={`/product/${sp.id}`}
              className="group fade-up"
              data-testid={`suggested-${sp.our_code}`}
            >
              <div className="aspect-[4/5] bg-[#F5F4EF] mb-3 overflow-hidden relative">
                <img
                  src={toAbs(sp.image_url || (sp.images || [])[0])}
                  alt={sp.name}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 bg-[#1E2C5A] text-[#C9A968] px-2 py-0.5 text-[10px] uppercase tracking-[0.15em]" data-testid={`suggested-code-${sp.our_code}`}>
                  {sp.our_code}
                </span>
              </div>
              <p className="label-eyebrow text-[10px]">{sp.size} · {(sp.surface || sp.category || "").toString().toUpperCase()}</p>
              <h3 className="font-editorial text-xl mt-1 tracking-editorial group-hover:text-[#1E2C5A] transition-colors">{sp.name}</h3>
              <p className="text-xs text-[#5C5C5C] mt-1">₹{sp.offer_price} <span className="text-[#8A8A8A]">/ {(sp.primary_uom || "BOX").toLowerCase()}</span></p>
              <p className="text-[10px] text-[#1E2C5A] uppercase tracking-[0.2em] mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Quick View →</p>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { customer, setShowRegister, patchCustomer } = useApp();
  const [p, setP] = useState(null);
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [calc, setCalc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [magnify, setMagnify] = useState(null);
  const [projects, setProjects] = useState([]);
  const [saveDlg, setSaveDlg] = useState(false);
  const [chosenProject, setChosenProject] = useState("");
  const [newProjName, setNewProjName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customer) {
      api.get("/projects").then(r => setProjects(r.data)).catch(() => {});
    }
  }, [customer]);

  useEffect(() => {
    api.get(`/products/${id}`).then(r => {
      setP(r.data);
      setActiveImage(r.data.image_url || (r.data.images || [])[0] || null);
      api.post("/product-views", { product_id: id }).catch(() => {});
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    const L = parseFloat(length), W = parseFloat(width);
    if (!p || !L || !W) { setCalc(null); return; }
    api.post("/calc/room", {
      length: L, width: W, tile_area_sqft: p.tile_area_sqft, packing: p.packing
    }).then(r => setCalc(r.data));
  }, [length, width, p]);

  const allImages = useMemo(() => {
    if (!p) return [];
    const seen = new Set();
    const list = [];
    for (const url of [p.image_url, ...(p.images || [])]) {
      if (url && !seen.has(url)) { seen.add(url); list.push(url); }
    }
    return list;
  }, [p]);

  const videos = (p && p.videos) || [];

  if (!p) return <div className="p-20 text-center text-[#8A8A8A]" data-testid="product-loading">Loading...</div>;

  const isWall = (p.tile_type || "floor") === "wall";
  const dim2Label = isWall ? "Height (ft)" : "Width (ft)";

  const addToCart = async () => {
    if (!customer) { setShowRegister(true); return; }
    if (!calc) { toast.error("Please enter dimensions first"); return; }
    const moq = Math.max(1, parseInt(p.moq) || 1);
    setBusy(true);
    const cart = [...(customer.cart || [])];
    const idx = cart.findIndex(c => c.product_id === p.id);
    const entry = {
      product_id: p.id,
      length: parseFloat(length),
      width_or_height: parseFloat(width),
      // Silently lift boxes to at least MOQ — buyer isn't asked to change room size.
      boxes: Math.max(moq, calc.boxes_required),
    };
    if (idx >= 0) cart[idx] = entry;
    else cart.push(entry);
    await patchCustomer({ cart, room_length: parseFloat(length), room_width: parseFloat(width), current_step: "cart" });
    setBusy(false);
    if (calc.boxes_required < moq) {
      toast.success(`Added to cart · ${moq} boxes (MOQ) applied automatically`);
    } else {
      toast.success("Added to cart");
    }
    nav("/cart");
  };

  const saveToProject = async () => {
    if (!customer) { setShowRegister(true); return; }
    if (!calc) { toast.error("Please enter dimensions first"); return; }
    const entry = {
      product_id: p.id,
      length: parseFloat(length),
      width_or_height: parseFloat(width),
      boxes: calc.boxes_required,
    };
    setSaving(true);
    try {
      if (chosenProject === "__new__") {
        if (!newProjName.trim()) { toast.error("Please name the new project"); setSaving(false); return; }
        await api.post("/projects", { name: newProjName.trim(), items: [entry] });
        toast.success(`Saved to new project "${newProjName}"`);
      } else if (chosenProject) {
        const proj = projects.find(pr => pr.id === chosenProject);
        if (!proj) { toast.error("Project not found"); setSaving(false); return; }
        const existingItems = proj.items || [];
        const idx = existingItems.findIndex(x => x.product_id === p.id);
        const nextItems = [...existingItems];
        if (idx >= 0) nextItems[idx] = entry; else nextItems.push(entry);
        await api.patch(`/projects/${chosenProject}`, { items: nextItems });
        toast.success(`Saved to "${proj.name}"`);
      } else {
        toast.error("Choose a project or create a new one");
        setSaving(false);
        return;
      }
      const r = await api.get("/projects");
      setProjects(r.data);
      setSaveDlg(false);
      setChosenProject("");
      setNewProjName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-12" data-testid="product-detail">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Media */}
        <div className="lg:col-span-7 space-y-6">
          <div
            className="aspect-[4/5] bg-[#F0EFEA] relative overflow-hidden cursor-zoom-in group"
            onClick={() => activeImage && setMagnify({ kind: "image", url: activeImage })}
            data-testid="product-hero-image"
          >
            {activeImage && <img src={toAbs(activeImage)} alt={p.our_code} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105 bg-[#F5F4EF]" />}
            <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1.5 text-xs uppercase tracking-[0.15em] flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn className="w-3.5 h-3.5" strokeWidth={1.5} /> Click to magnify
            </div>
            {p.ultra_premium && (
              <div className="absolute top-4 left-4 bg-[#1E2C5A] text-[#C9A968] px-4 py-2 flex items-center gap-2 border border-[#C9A968]/40" data-testid="tileroom-assured-badge">
                <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.2em] font-medium">TileRoom Assured</span>
              </div>
            )}
          </div>

          {(allImages.length > 1 || videos.length > 0) && (
            <div className="grid grid-cols-5 gap-2" data-testid="product-gallery">
              {allImages.map((url, i) => (
                <button
                  key={`img-${i}`}
                  type="button"
                  onClick={() => setActiveImage(url)}
                  className={`aspect-square bg-[#F0EFEA] overflow-hidden ${activeImage === url ? "ring-2 ring-[#C9A968]" : "opacity-80 hover:opacity-100"}`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={toAbs(url)} alt={`${p.our_code} view ${i+1}`} className="w-full h-full object-contain bg-[#F5F4EF]" />
                </button>
              ))}
              {videos.map((url, i) => (
                <button
                  key={`vid-${i}`}
                  type="button"
                  onClick={() => setMagnify({ kind: "video", url })}
                  className="aspect-square bg-black relative overflow-hidden group"
                  data-testid={`gallery-video-${i}`}
                >
                  <video src={toAbs(url)} className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-opacity" muted />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                      <div className="w-0 h-0 border-y-4 border-y-transparent border-l-6 border-l-[#1E2C5A] ml-0.5" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info sticky */}
        <div className="lg:col-span-5">
          <div className="sticky top-32">
            <p className="label-eyebrow">
              {p.category} · {p.size} · <span className="text-[#1E2C5A]">{(p.tile_type || "floor").toUpperCase()} TILE</span>
            </p>
            {p.ultra_premium && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-[#C9A968] text-xs uppercase tracking-[0.2em] border-b border-[#C9A968]/40 pb-1" data-testid="tileroom-assured-info">
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> TileRoom Assured
              </p>
            )}
            <h1 className="font-editorial text-5xl tracking-editorial leading-none mt-4" data-testid="product-name">{p.our_code}</h1>
            <div className="flex items-center gap-4 mt-6">
              <p className="text-3xl font-medium">
                ₹{p.offer_price}<span className="text-sm text-[#8A8A8A]"> /{(p.primary_uom || "BOX").toLowerCase()}</span>
                {p.tertiary_uom && Number(p.tertiary_uom_value) > 0 ? (
                  <span className="text-sm text-[#8A8A8A] ml-2">
                    (₹{(Number(p.offer_price) / Number(p.tertiary_uom_value)).toFixed(2)} / {p.tertiary_uom})
                  </span>
                ) : null}
              </p>
              <p className="text-lg text-[#8A8A8A] line-through">₹{p.mrp}</p>
              <p className="text-xs text-[#1E2C5A] tracking-wider uppercase">Save ₹{p.mrp - p.offer_price}</p>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-[#5C5C5C]">
              <Star className="w-4 h-4 fill-[#C9A968] text-[#C9A968]" strokeWidth={1.5} /> {p.rating}
              <span className="text-[#8A8A8A] ml-2">· Packing {p.packing} tiles/box</span>
            </div>

            <div className="mt-4 text-xs text-[#1E2C5A] uppercase tracking-[0.15em] border-l-2 border-[#C9A968] pl-3" data-testid="product-transit">
              Transit: {p.transit_min_days || 10}–{p.transit_max_days || 21} business days
            </div>
            {Number(p.moq || 1) > 1 && (
              <div className="mt-2 text-xs text-[#8B7A3E] uppercase tracking-[0.15em] border-l-2 border-[#C9A968] pl-3 bg-[#FDF9EE] py-1" data-testid="product-moq">
                Minimum Order: {p.moq} boxes
              </div>
            )}

            <p className="text-sm text-[#5C5C5C] mt-6 leading-relaxed">{p.description}</p>

            <div className="mt-10 border-t border-[#1A1A1A]/10 pt-8">
              <div className="flex items-center gap-2 mb-6">
                <Ruler className="w-4 h-4 text-[#1E2C5A]" strokeWidth={1.5} />
                <p className="label-eyebrow !text-[#1A1A1A]">
                  {isWall ? "Wall Measurement (L × H)" : "Floor Measurement (L × W)"}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="label-eyebrow block mb-1">Length (ft)</label>
                  <input type="number" className="input-editorial" value={length} onChange={(e) => setLength(e.target.value)} placeholder="0" data-testid="calc-length" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">{dim2Label}</label>
                  <input type="number" className="input-editorial" value={width} onChange={(e) => setWidth(e.target.value)} placeholder="0" data-testid="calc-width" />
                </div>
              </div>

              {calc && (
                <div className="mt-8 grid grid-cols-3 gap-4" data-testid="calc-results">
                  <div>
                    <p className="label-eyebrow">Total Area</p>
                    <p className="font-editorial text-3xl mt-1" data-testid="calc-area">{calc.total_area_sqft}</p>
                    <p className="text-xs text-[#8A8A8A]">sq ft</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Tiles</p>
                    <p className="font-editorial text-3xl mt-1" data-testid="calc-tiles">{calc.tiles_required}</p>
                    <p className="text-xs text-[#8A8A8A]">+10% wastage</p>
                  </div>
                  <div>
                    <p className="label-eyebrow">Boxes</p>
                    <p className="font-editorial text-3xl mt-1 text-[#1E2C5A]" data-testid="calc-boxes">{calc.boxes_required}</p>
                    <p className="text-xs text-[#8A8A8A]">required</p>
                  </div>
                </div>
              )}

              <button className="btn-clay w-full mt-10" onClick={addToCart} disabled={busy || !calc} data-testid="product-add-to-cart">
                Add to Cart <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <button
                className="w-full mt-3 bg-[#C9A968] text-[#1A1A1A] py-3.5 text-xs uppercase tracking-[0.2em] font-medium inline-flex items-center justify-center gap-2 hover:bg-[#B89550] transition-colors disabled:opacity-40"
                onClick={() => { if (!customer) { setShowRegister(true); return; } if (!calc) { toast.error("Please enter dimensions first"); return; } setSaveDlg(true); }}
                disabled={!calc}
                data-testid="product-save-to-project"
              >
                <FolderPlus className="w-4 h-4" strokeWidth={1.5} /> Save to a Project
              </button>
            </div>
          </div>
        </div>
      </div>

      {p.suggested_codes && p.suggested_codes.length > 0 && (
        <SuggestedProductsSection codes={p.suggested_codes} currentProductCode={p.our_code} />
      )}

      {/* Save to Project modal */}
      {saveDlg && (
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={() => setSaveDlg(false)} data-testid="save-project-modal">
          <div className="bg-[#FAF9F6] max-w-md w-full p-10 relative fade-up" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSaveDlg(false)} className="absolute top-4 right-4 text-[#5C5C5C]" data-testid="save-project-close">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <p className="label-eyebrow">Save Selection</p>
            <h3 className="font-editorial text-3xl mt-2 mb-6 tracking-editorial">Add to a project</h3>
            <p className="text-sm text-[#5C5C5C] mb-4">{p.our_code} · {calc?.boxes_required} boxes for {length}ft × {width}ft</p>

            <label className="label-eyebrow block mb-2">Choose Project</label>
            <select
              className="w-full h-10 border border-[#d6d3ce] bg-transparent px-3 text-sm mb-4"
              value={chosenProject}
              onChange={(e) => setChosenProject(e.target.value)}
              data-testid="save-project-select"
            >
              <option value="">Select an existing project...</option>
              {projects.map(pr => <option key={pr.id} value={pr.id}>{pr.name} ({pr.items?.length || 0} items)</option>)}
              <option value="__new__">+ Create New Project</option>
            </select>

            {chosenProject === "__new__" && (
              <div className="mb-4">
                <label className="label-eyebrow block mb-2">New Project Name</label>
                <input
                  className="input-editorial"
                  placeholder="e.g. Villa 12 · Bathroom"
                  value={newProjName}
                  onChange={(e) => setNewProjName(e.target.value)}
                  data-testid="save-project-new-name"
                  autoFocus
                />
              </div>
            )}

            <button onClick={saveToProject} disabled={saving || !chosenProject} className="btn-clay w-full mt-4" data-testid="save-project-submit">
              {saving ? "Saving..." : "Save to Project"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {/* Magnify lightbox */}
      {magnify && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setMagnify(null)}
          data-testid="magnify-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setMagnify(null); }}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            data-testid="magnify-close"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          {magnify.kind === "image" ? (
            <img
              src={toAbs(magnify.url)}
              alt="magnified view"
              className="max-w-[95vw] max-h-[92vh] object-contain select-none"
              onClick={(e) => e.stopPropagation()}
              data-testid="magnify-image"
            />
          ) : (
            <video
              src={toAbs(magnify.url)}
              controls
              autoPlay
              className="max-w-[95vw] max-h-[92vh]"
              onClick={(e) => e.stopPropagation()}
              data-testid="magnify-video"
            />
          )}
        </div>
      )}
    </div>
  );
}
